/**
 * companyProducts.js
 *
 * Which CorrGuard products the company currently being viewed actually has data
 * for.  The sidebar's Applications section and the router's product guards are
 * driven by these flags.
 *
 * Each probe is deliberately the cheapest question that answers "is there any
 * data": one page of runs, the CP survey-job catalogue, and a single XLI line.
 * Answers are cached per company (IndexedDB, stale-while-revalidate) so moving
 * between pages does not re-probe, and every probe resolves `false` rather than
 * rejecting — a failing product lookup must never blank the whole sidebar.
 */

import { ajaxNew as ajax } from '../../../../scanline/utils/ajax';
import { cacheKey, cachedQuery } from '../../../../scanline/utils/dataCache';
import { fetchCpSurveyJobs } from '../../pages/AllProjects/utils/cpSurveyApi';

/**
 * Survey types that make a line a Spectrum XLI project — the SURVEY_TYPE_CATEGORY
 * entries mapping to 'overline' (see pages/shared/projectFilterUtils).  Kept as a
 * literal list because it is sent to the `/lines` `type` filter, which matches
 * `survey_types.type` values directly.
 */
const XLI_SURVEY_TYPES = [
	'ON_OFF', 'ON', 'ON-OFF', 'DEPOL', 'DCVG', 'ACVG', 'REL',
	'NATIVE', 'ACCA', 'DOC', 'ACPSP', 'PROFILE', 'REPORTED_ANOMALIES',
	'ROW', 'UNKNOWN'
];

/** Answers are re-checked at most this often per company. */
const PRODUCTS_MAX_AGE_MS = 10 * 60 * 1000;

const NO_DATA = Object.freeze({ hasTracking: false, hasCp: false, hasXli: false });

/**
 * `undefined` scopes a request to every customer the account can see, which is
 * what an internal operator who has not switched company should get.
 */
const scopeName = company => {
	const name = String(company === null || company === undefined ? '' : company).trim();
	return name === '' || name === 'CorrproCanada' ? undefined : name;
};

/**
 * Run a request so that nothing it can do reaches the caller: the shared ajax
 * helper throws synchronously when no token is present, and a probe failing for
 * any reason must resolve to "unknown" (null) rather than break the sidebar.
 */
const swallow = request => {
	try {
		return Promise.resolve(request()).catch(() => null);
	} catch {
		return Promise.resolve(null);
	}
};

/** Any Armadillo Tracking run at all — one run is enough, so ask for one. */
const probeTracking = company =>
	swallow(() =>
		ajax({
			url: '/tracking-runs',
			data: JSON.stringify(
				scopeName(company) ? { limit: 1, clientName: scopeName(company) } : { limit: 1 }
			),
			method: 'POST',
			aip: 'assetview'
		})
	).then(res => {
		if (!res) {return false;}
		// The endpoint answers either with a bare array or with a delta-sync
		// envelope ({ runs, ... }); older responses arrive as a JSON string body.
		if (Array.isArray(res)) {return res.length > 0;}
		if (Array.isArray(res.runs)) {return res.runs.length > 0;}
		if (typeof res.body === 'string') {
			try {
				const parsed = JSON.parse(res.body);
				if (Array.isArray(parsed)) {return parsed.length > 0;}
				return Array.isArray(parsed && parsed.runs) && parsed.runs.length > 0;
			} catch {
				return false;
			}
		}
		return false;
	});

/** Any CorrMonitor CP survey job. */
const probeCp = company =>
	swallow(() => fetchCpSurveyJobs({ clientName: scopeName(company) })).then(
		jobs => Array.isArray(jobs) && jobs.length > 0
	);

/** Any Spectrum XLI line — the `type` filter does the work server-side. */
const probeXli = company =>
	swallow(() =>
		ajax({
			url: '/lines',
			data: JSON.stringify({
				limit: 1,
				offset: 0,
				customer: scopeName(company),
				type: XLI_SURVEY_TYPES
			}),
			method: 'POST',
			aip: 'scanline'
		})
	).then(res => Array.isArray(res) && res.length > 0);

// In-flight lookups by cache key.  The sidebar and the router both ask for the
// flags on a cold start; without this they would each fire the full probe set.
const inFlight = new Map();

/**
 * Products the given company has data for, cache-first.
 *
 * @param {object}   [options]
 * @param {string}   [options.company]  Company being viewed; omit for "everything
 *   this account can see".
 * @param {Function} [options.onCached] Invoked with the cached answer (if any) so
 *   the sidebar can render without waiting on the network.
 * @returns {Promise<{hasTracking: boolean, hasCp: boolean, hasXli: boolean}>}
 */
export const fetchCompanyProducts = ({ company, onCached } = {}) => {
	const key = cacheKey('company-products', 'v1', scopeName(company) || 'all');
	const pending = inFlight.get(key);
	if (pending) {
		return onCached ? pending.then(res => { onCached(res); return res; }) : pending;
	}
	const request = cachedQuery({
		key,
		maxAgeMs: PRODUCTS_MAX_AGE_MS,
		onCached: cached => {
			if (onCached && cached && typeof cached === 'object') {onCached(cached);}
		},
		fetcher: () =>
			Promise.all([
				probeTracking(company),
				probeCp(company),
				probeXli(company)
			]).then(([hasTracking, hasCp, hasXli]) => ({ hasTracking, hasCp, hasXli }))
	})
		.then(res => (res && typeof res === 'object' ? res : NO_DATA))
		.finally(() => inFlight.delete(key));

	inFlight.set(key, request);
	return request;
};

export { NO_DATA as NO_COMPANY_PRODUCTS };

export default fetchCompanyProducts;
